import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { toast } from 'react-toastify';
import Button from './Button';
import { InputGroup, FieldGroup } from './Input';
import Checkbox from './Checkbox';
import SelectGames from './SelectGames';
import TagsSelect from './TagsSelect';
import ENV_CONFIG from '../config/environment-unified';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.color.background};
  border: 1px solid #333;
  border-radius: 12px;
  padding: 30px;
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  color: ${({ theme }) => theme.color.primary};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #888;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: #fff;
  }
`;

const OptionCard = styled.div`
  background: ${props => props.selected ? '#2a2a2a' : '#1a1a1a'};
  border: 2px solid ${props => props.selected ? props.theme.color.primary : '#333'};
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 15px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: ${props => props.theme.color.primary};
    background: #2a2a2a;
  }
`;

const OptionTitle = styled.h3`
  color: ${({ theme }) => theme.color.primary};
  margin: 0 0 10px 0;
  font-size: 1.2rem;
`;

const OptionDescription = styled.p`
  color: #888;
  margin: 0;
  font-size: 0.9rem;
`;

const FormSection = styled.div`
  margin-top: 20px;
`;

const SectionTitle = styled.h4`
  color: ${({ theme }) => theme.color.text};
  margin: 20px 0 10px 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;

const GoLiveModal = ({ isOpen, onClose, eventSlug, onGoLive }) => {
  // Mandatory: every stream must be linked to an active event
  const [mode, setMode] = useState('event'); // keep state for minimal refactor
  const [loading, setLoading] = useState(false);
  // const [games, setGames] = useState([]);
  const [eventData, setEventData] = useState(null);
  const didInitRestreamRef = useRef(false);
  const userTouchedRestreamRef = useRef(false);

  const [restreamProfileCfg, setRestreamProfileCfg] = useState({
    loading: false,
    loaded: false,
    enabled: false,
    twitchEnabled: false,
    youtubeEnabled: false,
    facebookEnabled: false
  });

  const [streamConfig, setStreamConfig] = useState({
    title: '',
    gameId: '',
    gameName: '',
    tags: [], // Array of tag objects {label, value}
    enableRestream: false
  });

  // Keep restream toggle in sync with Profile (Settings → Multi‑streaming)
  useEffect(() => {
    if (!isOpen) return;

    // Reset init flags each time modal opens (or event changes)
    didInitRestreamRef.current = false;
    userTouchedRestreamRef.current = false;

    let mounted = true;
    setRestreamProfileCfg(prev => ({ ...prev, loading: true }));
    axios.get('/api/stream/restream/config', { withCredentials: true })
      .then(res => {
        if (!mounted) return;
        const cfg = res.data || {};
        setRestreamProfileCfg({
          loading: false,
          loaded: true,
          enabled: !!cfg.enabled,
          twitchEnabled: !!cfg.twitchEnabled,
          youtubeEnabled: !!cfg.youtubeEnabled,
          facebookEnabled: !!cfg.facebookEnabled
        });
      })
      .catch(() => {
        if (!mounted) return;
        setRestreamProfileCfg(prev => ({ ...prev, loading: false, loaded: true }));
      });

    return () => { mounted = false; };
  }, [isOpen, eventSlug]);

  // Fetch event data if eventSlug is provided
  useEffect(() => {
    if (eventSlug && isOpen) {
      axios.get(`${ENV_CONFIG.EVENTS_ENDPOINT}/events/${eventSlug}`, { withCredentials: true })
        .then(res => {
          const event = res.data.event;
          setEventData(event);
          // Pre-fill with event data
          // Convert tags array to objects if needed
          const tagsObjects = (event.tags || []).map(tag =>
            typeof tag === 'string' ? { label: tag, value: tag } : tag
          );
          setStreamConfig({
            title: event.name || '',
            gameId: event.gameId || '',
            gameName: '', // Will be filled by game name
            tags: tagsObjects,
            // enableRestream is initialized once we also know the Profile setting (see effect below)
            enableRestream: false
          });
        })
        .catch(err => {
          console.error('Failed to load event:', err);
          toast.error('Failed to load event details');
        });
    }
  }, [eventSlug, isOpen]);

  // Initialize Enable Restream once we know both Event defaults and Profile config.
  useEffect(() => {
    if (!isOpen) return;
    if (!eventData) return;
    if (!restreamProfileCfg.loaded) return;
    if (didInitRestreamRef.current) return;
    if (userTouchedRestreamRef.current) return;

    const eventDefault = !!eventData?.options?.restream;
    const profileDefault = !!restreamProfileCfg.enabled;
    const effectiveDefault = eventDefault || profileDefault;

    didInitRestreamRef.current = true;
    setStreamConfig(prev => ({ ...prev, enableRestream: effectiveDefault }));
  }, [isOpen, eventData, restreamProfileCfg.loaded, restreamProfileCfg.enabled]);

  // Fetch games list
  // useEffect(() => {
  //   if (isOpen) {
  //     axios.get(ENV_CONFIG.GAMES_ENDPOINT)
  //       .then(res => setGames(res.data || []))
  //       .catch(err => console.error('Failed to load games:', err));
  //   }
  // }, [isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setStreamConfig(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  /*
  // NOTE: Disabled as requested.
  // Previously, toggling Restream in the Go Live popup updated the Profile restream config
  // (and could trigger restream key/config refresh). We keep the code commented for reference.
  const persistProfileRestreamEnabled = async (enabled) => {
    try {
      // Ensure we have the latest platform toggles so we don't accidentally turn them off.
      let cfg = restreamProfileCfg;
      if (!cfg.loaded) {
        const res = await axios.get('/api/stream/restream/config', { withCredentials: true });
        const fresh = res.data || {};
        cfg = {
          ...cfg,
          loaded: true,
          enabled: !!fresh.enabled,
          twitchEnabled: !!fresh.twitchEnabled,
          youtubeEnabled: !!fresh.youtubeEnabled,
          facebookEnabled: !!fresh.facebookEnabled,
        };
      }

      await axios.put('/api/stream/restream/config', {
        enabled: !!enabled,
        twitchEnabled: !!cfg.twitchEnabled,
        youtubeEnabled: !!cfg.youtubeEnabled,
        facebookEnabled: !!cfg.facebookEnabled
      }, { withCredentials: true });

      setRestreamProfileCfg(prev => ({
        ...prev,
        loading: false,
        loaded: true,
        enabled: !!enabled,
        twitchEnabled: !!cfg.twitchEnabled,
        youtubeEnabled: !!cfg.youtubeEnabled,
        facebookEnabled: !!cfg.facebookEnabled
      }));
    } catch (e) {
      toast.error('Failed to update Restream setting. Please check Settings → Multi‑streaming.');
    }
  };
  */

  const handleRestreamToggle = (e) => {
    const checked = !!e?.target?.checked;
    userTouchedRestreamRef.current = true;
    setStreamConfig(prev => ({ ...prev, enableRestream: checked }));
    // Disabled as requested (no restream config/key update from Go Live popup)
    // await persistProfileRestreamEnabled(checked);
  };

  const handleGoLive = async () => {
    if (!streamConfig.title) {
      toast.error('Please enter a stream title');
      return;
    }

    if (!eventSlug || !eventData?.id) {
      toast.error('Please start streaming from an active event.');
      return;
    }

    setLoading(true);
    try {
      // Convert tags from objects to array of strings
      const tagsArray = streamConfig.tags.map(tag => tag.value || tag.label || tag);

      const payload = {
        title: streamConfig.title,
        gameId: streamConfig.gameId || null,
        tags: tagsArray,
        enableRestream: streamConfig.enableRestream,
        // Backend expects the event UUID, not the slug
        eventId: eventData.id
      };

      // Call the parent callback with the config
      await onGoLive(payload);

      onClose();
    } catch (error) {
      console.error('Error starting stream:', error);
      toast.error('Failed to start stream');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <Title>🎥 Go Live</Title>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        {/* Mode Selection */}
        {eventSlug && (
          <>
            <SectionTitle>Choose Stream Mode</SectionTitle>

            <OptionCard
              selected={mode === 'event'}
              onClick={() => setMode('event')}
            >
              <OptionTitle>📺 Stream to Event: {eventData?.name}</OptionTitle>
              <OptionDescription>
                Stream to your event "{eventData?.name}". Event settings will be used as defaults.
              </OptionDescription>
            </OptionCard>

            {/* <OptionCard 
              selected={mode === 'new'} 
              onClick={() => setMode('new')}
            >
              <OptionTitle>🆕 New Stream Session</OptionTitle>
              <OptionDescription>
                Start a new stream session without linking to an event. Configure everything from scratch.
              </OptionDescription>
            </OptionCard> */}
          </>
        )}

        {/* Stream Configuration */}
        <FormSection>
          <SectionTitle>Stream Configuration</SectionTitle>

          <FieldGroup label="Stream Title">
            <InputGroup
              name="title"
              value={streamConfig.title}
              onChange={handleChange}
              placeholder="Enter stream title"
              required
            />
          </FieldGroup>

          <FieldGroup label="Game">
            {streamConfig.gameId !== 'random' ? (
              <SelectGames
                placeholder="Search for a game or select Random"
                id="golive-game-select"
                data={streamConfig}
                setdata={(gameData) => {
                  if (gameData === 'random') {
                    setStreamConfig({ ...streamConfig, gameId: 'random', gameName: 'Random' });
                  } else {
                    setStreamConfig({ ...streamConfig, gameId: gameData.gameId, gameName: gameData.gameName });
                  }
                }}
              />
            ) : (
              <div style={{
                padding: '10px',
                background: '#2a2a2a',
                border: '1px solid #444',
                borderRadius: '8px',
                color: '#888'
              }}>
                🎲 Random Game Selected - Game input disabled
              </div>
            )}
            {/* Random option button */}
            <div style={{ marginTop: '10px', display: 'flex', gap: '10px', alignItems: 'center' }}>
              <Button
                type="button"
                onClick={() => setStreamConfig({ ...streamConfig, gameId: 'random', gameName: 'Random' })}
                style={{ background: '#666', padding: '8px 16px', fontSize: '0.9rem' }}
              >
                🎲 Random Game
              </Button>
              {streamConfig.gameId === 'random' && (
                <Button
                  type="button"
                  onClick={() => setStreamConfig({ ...streamConfig, gameId: '', gameName: '' })}
                  style={{ background: '#EF4444', padding: '8px 16px', fontSize: '0.9rem' }}
                >
                  ✕ Clear Random
                </Button>
              )}
              {streamConfig.gameName && streamConfig.gameId !== 'random' && (
                <span style={{ color: '#10B981' }}>
                  Selected: {streamConfig.gameName}
                </span>
              )}
            </div>
          </FieldGroup>

          <FieldGroup label="Tags">
            <TagsSelect
              tags={streamConfig.tags}
              settags={(newTags) => setStreamConfig({ ...streamConfig, tags: newTags })}
              maxTags={10}
              placeholder="Add tags (press Enter)"
            />
          </FieldGroup>

          <div style={{ marginTop: '15px' }}>
            <Checkbox
              name="enableRestream"
              checked={streamConfig.enableRestream}
              onChange={handleRestreamToggle}
            >
              Enable Restream
            </Checkbox>
            <p style={{ color: '#888', fontSize: '0.85rem', marginTop: '5px', marginLeft: '25px' }}>
              Stream to your configured platforms (Facebook, Twitch, YouTube) from Settings → Profile.
              {mode === 'event' && eventData?.options?.restream && ' Event has restream enabled by default.'}
            </p>
          </div>
        </FormSection>

        {/* Actions */}
        <ButtonGroup>
          <Button onClick={onClose} variant="secondary" disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleGoLive} disabled={loading}>
            {loading ? 'Starting...' : '🚀 Go Live'}
          </Button>
        </ButtonGroup>
      </ModalContent>
    </ModalOverlay>
  );
};

export default GoLiveModal;

